import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { GalleryForm } from '../components/GalleryForm';
import { GalleryCard } from '../components/GalleryCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useGalleries } from '../hooks/useGalleries';
import { PhotoStreamClient } from '../api/PhotoStreamClient';
import { Gallery } from '../api/types';

const apiClient = new PhotoStreamClient(
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'
);

export const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { galleries, loading, error, loadGalleries } = useGalleries(apiClient);

  useEffect(() => {
    loadGalleries();
  }, [loadGalleries]);

  const handleGalleryCreated = () => {
    loadGalleries(); // Refresh gallery list
    setTimeout(() => navigate('/'), 2000); // Navigate to home after delay
  };

  const handleGalleryClick = (gallery: Gallery) => {
    navigate(`/gallery/${gallery.id}`);
  };

  const handleRetry = () => {
    loadGalleries();
  };

  return (
    <div className="container mx-auto py-8">
      <div className="mb-6">
        <Button variant="outline" asChild>
          <Link to="/">← Back to Home</Link>
        </Button>
      </div>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">Admin Panel</h1>
        <p className="text-muted-foreground">Manage your PhotoStream galleries</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <GalleryForm onSuccess={handleGalleryCreated} />
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Existing Galleries ({galleries.length})</CardTitle>
              <CardDescription>
                Click on a gallery to view and manage it
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-destructive text-sm mb-2">{error}</p>
                  <Button onClick={handleRetry} variant="outline" size="sm">
                    Try Again
                  </Button>
                </div>
              )}

              {loading && <LoadingSpinner />}

              {!loading && !error && galleries.length === 0 && (
                <p className="text-muted-foreground text-center py-8">
                  No galleries created yet. Create your first gallery using the form.
                </p>
              )}

              {!loading && galleries.length > 0 && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {galleries.map((gallery) => (
                    <div key={gallery.id} className="cursor-pointer">
                      <GalleryCard
                        gallery={gallery}
                        onClick={handleGalleryClick}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
